import { useState } from 'react';
import { useT } from '@/store';
import { useAllVehicles } from '@/hooks/useFleet';
import { useDeleteServicePlan, useServicePlans, useUpdateServicePlan } from '@/hooks/useServicing';
import { Panel, EmptyState, ErrorState, SkeletonRows } from '@/components/ui/Panel';
import { ConfirmButton } from '@/components/ui/ConfirmButton';
import { ServiceFormModal } from '@/components/fleet/ServiceFormModal';
import { formatKm } from '@/lib/format';

/**
 * The rules themselves, and the three things you can do to one.
 *
 * ServicePanel above shows what every rule COMPUTES — which trucks are due,
 * how overdue, on what basis. It has never shown the rules, and nothing in
 * the console could change one. That gap had a specific cost: the route
 * refuses a duplicate plan with a 409 that says, in the carrier's own
 * language, "redaguokite jį, o ne kurkite antrą" — edit that one instead of
 * adding a second. The advice was correct and there was nowhere to take it,
 * so the only way out was a second plan under a slightly different name,
 * which is precisely the split history the unique index exists to prevent.
 *
 * PAUSE IS NOT DELETE, AND BOTH ARE OFFERED
 * ------------------------------------------
 * `active: false` keeps the intervals and stops the deadline. A truck sold,
 * a job moved to a contract that covers it, a rule being reconsidered — all
 * of those want the figures kept. Delete is a hard delete and is right when
 * the rule was a mistake. Offering only one of them makes people use it for
 * both, and the one they would reach for is delete.
 *
 * Deleting a plan does NOT touch the history. Service records carry their own
 * service_key rather than a plan_id, so what disappears is the future
 * deadline — which is what somebody deleting a rule is asking for — and the
 * record of what was actually done to the truck stays. Said on screen, not
 * only here: a carrier hesitating over the delete button is hesitating
 * because he cannot tell which of the two he is about to lose.
 */

const EM_DASH = '—';

/** The same sizing ConfirmButton applies to itself, so the three actions on a
 *  row are one control strip rather than two sizes of button. There is no
 *  `btn-sm` in index.css — this is the shape the rest of the app writes out
 *  inline, kept in one constant here because it appears twice. */
const ACTION_BTN = 'btn !px-2.5 !py-1 !text-[11px]';

/** Both intervals, or the one that is set. A plan always has at least one —
 *  schema.sql's CHECK — so this never returns an empty string. */
function intervalText(t, plan) {
  const parts = [];
  if (plan.interval_km !== null) parts.push(t('servicing.everyKm', { n: formatKm(plan.interval_km) }));
  if (plan.interval_months !== null) parts.push(t('servicing.everyMonths', { n: plan.interval_months }));
  return parts.join(' · ');
}

function PlanRow({ plan, plateFor, onEdit }) {
  const t = useT();
  const update = useUpdateServicePlan();
  const remove = useDeleteServicePlan();
  const busy = update.isPending || remove.isPending;

  return (
    <tr className={plan.active ? '' : 'opacity-60'}>
      <td className="py-1.5 pr-3">
        {plan.vehicle_id ? (plateFor(plan.vehicle_id) ?? EM_DASH) : t('servicePlans.scopeFleet')}
      </td>
      <td className="py-1.5 pr-3 font-medium">{plan.service_type}</td>
      <td className="py-1.5 pr-3 tabular-nums">{intervalText(t, plan)}</td>
      <td className="py-1.5 pr-3 tabular-nums text-dim">
        {t('servicePlans.noticeBoth', {
          d: plan.notice_days, km: formatKm(plan.notice_km),
        })}
      </td>
      <td className="py-1.5 pr-3">
        {plan.active ? null : (
          <span className="rounded-full bg-slate-500/15 px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.03em] text-dim">
            {t('servicePlans.paused')}
          </span>
        )}
      </td>
      <td className="py-1.5 text-right whitespace-nowrap">
        <div className="inline-flex items-center gap-1">
          <button type="button" className={ACTION_BTN} onClick={() => onEdit(plan)}>
            {t('common.editBtn')}
          </button>
          {/* Pause before delete, and in that order on the row too. `active:
              false` keeps the intervals and stops the deadline — a truck
              sold, a job moved onto a contract that covers it — and a list
              offering only delete makes people use delete for both. */}
          <button
            type="button"
            className={ACTION_BTN}
            disabled={busy}
            onClick={() => update.mutate({ planId: plan.plan_id, active: !plan.active })}
          >
            {plan.active ? t('servicePlans.pauseBtn') : t('servicePlans.resumeBtn')}
          </button>
          {/* The hint says what is NOT lost. Service records carry their own
              service_key rather than a plan_id, so the history survives a
              deleted rule — and a carrier who cannot tell which of the two
              he is about to lose does not press either button. */}
          <ConfirmButton
            label={t('common.deleteBtn')}
            confirmLabel={t('common.confirmDelete')}
            hint={t('servicePlans.deleteKeepsHistory')}
            disabled={busy}
            onConfirm={() => remove.mutate(plan.plan_id)}
          />
        </div>
      </td>
    </tr>
  );
}

export function ServicePlanList() {
  const t = useT();
  const plans = useServicePlans();
  const { data: vehicles = [] } = useAllVehicles();
  const [editing, setEditing] = useState(null);

  const plateFor = (vehicleId) =>
    vehicles.find((v) => v.vehicle_id === vehicleId)?.license_plate;

  const rows = plans.data ?? [];

  return (
    <Panel title={t('servicePlans.title')}>
      {plans.isError ? <ErrorState error={plans.error} /> : null}
      {plans.isLoading ? (
        <SkeletonRows rows={3} />
      ) : (
        <>
          <p className="mb-3 text-[11px] leading-snug text-dim">{t('servicePlans.intro')}</p>
          {rows.length === 0 ? (
            <EmptyState>{t('servicePlans.none')}</EmptyState>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-[11.5px]">
                <thead className="text-[9.5px] uppercase tracking-[0.03em] text-dim">
                  <tr>
                    <th className="pb-1 pr-3 font-bold">{t('servicePlans.colScope')}</th>
                    <th className="pb-1 pr-3 font-bold">{t('servicing.colService')}</th>
                    <th className="pb-1 pr-3 font-bold">{t('servicePlans.colInterval')}</th>
                    <th className="pb-1 pr-3 font-bold">{t('servicePlans.colNotice')}</th>
                    <th className="pb-1 pr-3 font-bold" />
                    <th className="pb-1 font-bold" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((plan) => (
                    <PlanRow
                      key={plan.plan_id}
                      plan={plan}
                      plateFor={plateFor}
                      onEdit={setEditing}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {editing ? (
        <ServiceFormModal mode="plan" plan={editing} onClose={() => setEditing(null)} />
      ) : null}
    </Panel>
  );
}
